import { DownloadOutlined, DeleteFilled, EditOutlined } from "@ant-design/icons";
import { Button, Card, Col, Descriptions, Input, Modal, Row, Typography } from "antd";
import axios from "axios";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { checkFolderPermission, deleteFile, renameFile, setSliceCurrentFolderId } from "../../redux/rtk/features/document/documentSlice";
import getUserFromToken from "../../utils/getUserFromToken";
import { toast } from "react-toastify";

const FileViewer = () => {
    const location = useLocation();
    const file = location.state?.file;
    const parentFolderId = location.state?.parentFolderId;
    const [previewUrl, setPreviewUrl] = useState("");
    const [isDeleted, setIsDeleted] = useState(false);
    const [isRenameModalVisible, setIsRenameModalVisible] = useState(false);
    const [newFileName, setNewFileName] = useState("");
    const [isRenaming, setIsRenaming] = useState(false);
    
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const userId = getUserFromToken();
    const permission = useSelector((state) => state?.document?.permission);

    const handleDeleteFile = async (fileId) => {
        const isConfirmed = window.confirm("Are you sure you want to delete this file?");
        if (isConfirmed) {
            try {
                await dispatch(deleteFile({ fileId }));
                toast.success("File deleted successfully!");
                setIsDeleted(true);
            } catch (err) {
                toast.error("Error deleting file.");
            }
        } else {
            alert("File deletion canceled.");
        }
    };

    const handleRenameFile = () => {
        if (!newFileName || newFileName.trim() === "") {
            alert("New file name is mandatory.");
            return;
        }

        setIsRenaming(true);
        
        // Get file name without extension
        const fileExtension = file.folderName.match(/\.[^.]+$/)?.[0] || "";
        const currentFileName = file.folderName;
        
        dispatch(renameFile({
            currentFileName,
            newFileName: newFileName.includes(fileExtension) ? newFileName : newFileName,
            folderId: parentFolderId || null
        }))
        .unwrap()
        .then((response) => {
            setIsRenameModalVisible(false);
            setIsRenaming(false);
            // Update the file object with new name
            if (response && response.updatedFile) {
                file.folderName = response.updatedFile.name;
                toast.success("File renamed successfully!");
            }
        })
        .catch((error) => {
            setIsRenaming(false);
            toast.error(error?.message || "Error renaming file.");
        });
    };

    useEffect(() => {
        dispatch(checkFolderPermission({ folderId: parentFolderId, userId: userId })).unwrap();
    }, [dispatch, parentFolderId, userId]);

    const handleGoBack = async () => {
        try {
            if (parentFolderId) {
                dispatch(setSliceCurrentFolderId(parentFolderId));
            } else {
                console.error("Folder not found for currentFolderId:", parentFolderId);
            }
    
            // Navigate after setting the folder ID
            navigate("/admin/FolderManager", { state: { parentFolderId: parentFolderId || 0 } });
    
        } catch (error) {
            console.error('Error navigating back:', error);
            // message.error('Failed to navigate back');
        }
    };

    useEffect(() => {
        const fetchFile = async () => {
            if (!file?.id || isDeleted) return; // Don't fetch if the file is deleted

            const token = localStorage.getItem("access-token"); // Get the token
            if (!token) {
                console.error("Authorization token missing");
                return;
            }

            try {
                const response = await axios.get(`document-proof/${file.id}`, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                    responseType: 'blob',
                });
                const objectUrl = URL.createObjectURL(response.data);
                setPreviewUrl(objectUrl); // Store for preview
            } catch (error) {
                console.error("Error fetching file:", error);
            }
        };

        fetchFile();
    }, [file?.id, isDeleted]); // Add isDeleted to the dependency array

    if (!file || isDeleted) {
        return (
            <Card className="w-full mt-4">
                <Button
                    type="primary"
                    onClick={handleGoBack}
                    style={{ marginTop: 10, marginBottom: 10 }}
                >
                    Back
                </Button>
                <Typography.Title level={4}>File deleted successfully.</Typography.Title>
            </Card>
        );
    }

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    };

    const handleDownload = async (fileId) => {
        try {
            const token = localStorage.getItem("access-token");
            if (!token) {
                console.error("Authorization token missing");
                return;
            }

            const response = await axios.get(`document-proof/${fileId}`, {
                headers: {
                    Authorization: `Bearer ${token}`, // Pass token in header
                },
                responseType: 'blob', // Handle response as binary data
            });

            const url = window.URL.createObjectURL(response.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = file.name || "document";
            document.body.appendChild(a);
            a.click();
            a.remove();
        } catch (error) {
            console.error("Error downloading file:", error);
        }
    };

    const showRenameModal = () => {
        // Extract filename without extension to pre-populate the input
        const fileExtension = file.folderName.match(/\.[^.]+$/)?.[0] || "";
        const baseFileName = file.folderName.replace(fileExtension, "");
        setNewFileName(baseFileName);
        setIsRenameModalVisible(true);
    };

    return (
        <Card className="w-full mt-4">
            <Button
                type="primary"
                onClick={handleGoBack}
                style={{ marginTop: 10, marginBottom: 10 }}
            >
                Back
            </Button>
            <div style={{ marginBottom: "16px" }} />
            <Row gutter={24}>
                {/* File Preview Card */}
                <Col span={12}>
                    <Card
                        title={file.folderName}
                        className="h-full flex flex-col items-center justify-center p-8 bg-gray-50"
                    >
                        {/* Check if the file is an image */}
                        {file.folderName?.match(/\.(jpeg|jpg|png|gif)$/i) ? (
                            <img
                                src={previewUrl}
                                alt={file.folderName}
                                className="mb-4"
                                style={{ maxHeight: '200px', objectFit: 'cover' }}
                            />
                        ) : file.folderName?.match(/\.(pdf)$/i) ? (
                            <iframe
                                src={previewUrl}
                                title={file.folderName}
                                width="100%"
                                height="250px"
                                className="mb-4"
                            />
                        ) : (
                            <div className="text-l text-blue-500 mb-4">
                                Preview not available
                            </div>
                        )}

                        <Typography.Title level={4} className="text-center mb-4">
                            {file.folderName}
                        </Typography.Title>
                        <Button
                            type="primary"
                            icon={<DownloadOutlined />}
                            onClick={() => handleDownload(file.id)}
                            className="mt-4"
                        >
                            Download File
                        </Button>
                    </Card>
                </Col>

                {/* File Details Card */}
                <Col span={12}>
                    <Card 
                        title="File Details" 
                        className="h-full" 
                        extra={
                            permission?.hasPermission && (
                                <div style={{ display: 'flex', gap: '16px' }}>
                                    <EditOutlined
                                        onClick={showRenameModal}
                                        style={{
                                            fontSize: "24px",
                                            color: "#1890ff",
                                            cursor: "pointer"
                                        }}
                                    />
                                    <DeleteFilled
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleDeleteFile(file?.id);
                                        }}
                                        style={{
                                            fontSize: "24px",
                                            color: "red",
                                            cursor: "pointer"
                                        }}
                                    />
                                </div>
                            )
                        }
                    >
                        <Descriptions column={1}>
                            <Descriptions.Item label="File Name">{file.folderName}</Descriptions.Item>
                            <Descriptions.Item label="Upload Date">{formatDate(file.createdAt)}</Descriptions.Item>
                        </Descriptions>
                    </Card>
                </Col>
            </Row>

            {/* Rename Modal */}
            <Modal
                title="Rename File"
                open={isRenameModalVisible}
                onCancel={() => setIsRenameModalVisible(false)}
                onOk={handleRenameFile}
                confirmLoading={isRenaming}
            >
                <div style={{ marginBottom: 16 }}>
                    <p>Enter new file name:</p>
                    <Input
                        value={newFileName}
                        onChange={(e) => setNewFileName(e.target.value)}
                        placeholder="New file name"
                    />
                    <p style={{ marginTop: 8, color: "#888" }}>
                        Original extension will be preserved.
                    </p>
                </div>
            </Modal>
        </Card>
    );
};

export default FileViewer;